import { ApiClient } from './apiClient'
import { authService } from './authService'
import type {
  TpaSession,
  TpaSessionResults,
  TuckmanScore,
  LencioniScore,
  SystemicScore,
  HPTScore,
  UserTPAScore,
  TeamTPAScore,
} from '@/types/tpaSession'
import type { TpaSessionStats, RoleTPAScore } from '@/types/tpaSessionStats'
import type { TpaSessionAdvice } from '@/types/tpaSessionAdvice'
import type { TeamPowerAdvice } from '@/types/teamPowerAdvice'
import type { TpaSessionFetchStatusResponse } from '@/types/tpaSession'
import type { TeamRole, TeamUser, TestEmailResult } from '@/types/teamUser'
import type {
  HistoricalSessionEntry,
  HistoricalTeamResponse,
  HPTDimensionMap,
} from '@/types/historicalSession'
import type { TeamUserAddPayload } from '@/types/teamUser'



// ── Mapping helpers ────────────────────────────────────────────────────────────

const toNumber = (value: unknown, fallback = 0): number => {
  const num = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(num) ? num : fallback
}

const mapTuckmanScore = (score: any): TuckmanScore => ({
  forming: toNumber(score?.forming),
  storming: toNumber(score?.storming),
  norming: toNumber(score?.norming),
  performing: toNumber(score?.performing),
  stage: score?.stage ?? 'Forming',
  score: toNumber(score?.score),
  primaryStage: score?.primaryStage ?? 'Forming',
  primaryStageScore: toNumber(score?.primaryStageScore),
  secondaryStage: score?.secondaryStage ?? 'Forming',
  secondaryStageScore: toNumber(score?.secondaryStageScore),
  adjusted_state: score?.adjusted_state ?? 'Forming',
  levels: score?.levels ?? {},
  aspects: score?.aspects ?? [],
})

const mapLencioniScore = (score: any): LencioniScore => ({
  lack_of_trust: toNumber(score?.lack_of_trust),
  fear_of_conflict: toNumber(score?.fear_of_conflict),
  lack_of_commitment: toNumber(score?.lack_of_commitment),
  avoidance_of_responsibility: toNumber(score?.avoidance_of_responsibility),
  lack_of_focus_on_results: toNumber(score?.lack_of_focus_on_results),
  dysfunction: score?.dysfunction ?? 'No Dysfunction',
  state: score?.state ?? 'Healthy',
  delta: toNumber(score?.delta),
  transition: Boolean(score?.transition),
  levels: score?.levels ?? {},
})

const mapSystemicScore = (score: any): SystemicScore => ({
  balance: toNumber(score?.balance),
  belonging: toNumber(score?.belonging),
  order: toNumber(score?.order),
  category_balance: score?.category_balance ?? 'Low',
  category_belonging: score?.category_belonging ?? 'Low',
  category_order: score?.category_order ?? 'Low',
  all_high: Boolean(score?.all_high),
  all_low: Boolean(score?.all_low),
  balance_low_others_high: Boolean(score?.balance_low_others_high),
  belonging_high_order_lower: Boolean(score?.belonging_high_order_lower),
  order_high_belonging_lower: Boolean(score?.order_high_belonging_lower),
})

const mapHPTScore = (score: any): HPTScore => ({
  adaptability_resilience: toNumber(score?.adaptability_resilience),
  conflict_management: toNumber(score?.conflict_management),
  effective_communication: toNumber(score?.effective_communication),
  results_orientation: toNumber(score?.results_orientation),
  roles_responsibilities: toNumber(score?.roles_responsibilities),
  shared_leadership: toNumber(score?.shared_leadership),
  shared_purpose: toNumber(score?.shared_purpose),
  trust_cohesion: toNumber(score?.trust_cohesion),
})

const mapUserTPAScore = (score: any): UserTPAScore => ({
  userId: score?.user_id ?? 0,
  userRole: score?.user_role ?? 'team_member',
  tuckman: mapTuckmanScore(score?.tuckman),
  lencioni: mapLencioniScore(score?.lencioni),
  systemic: mapSystemicScore(score?.systemic),
  hptEmpirical: mapHPTScore(score?.hpt_empirical),
  tpi: toNumber(score?.tpi),
  hptTheoretical: mapHPTScore(score?.hpt_theoretical),
})

const mapTeamTPAScore = (score: any): TeamTPAScore => ({
  tuckman: mapTuckmanScore(score?.tuckman),
  lencioni: mapLencioniScore(score?.lencioni),
  systemic: mapSystemicScore(score?.systemic),
  hptEmpirical: mapHPTScore(score?.hpt_empirical),
  tpi: toNumber(score?.tpi),
  hptTheoretical: mapHPTScore(score?.hpt_theoretical),
})

const mapRoleTPAScore = (score: any): RoleTPAScore => ({
  role: score?.role ?? 'team_member',
  count: toNumber(score?.count),
  tuckman: mapTuckmanScore(score?.tuckman),
  lencioni: mapLencioniScore(score?.lencioni),
  systemic: mapSystemicScore(score?.systemic),
  hptEmpirical: mapHPTScore(score?.hpt_empirical),
  tpi: toNumber(score?.tpi),
  hptTheoretical: mapHPTScore(score?.hpt_theoretical),
})

const mapSessionStats = (stats: any): TpaSessionStats | null => {
  if (!stats) return null
  return {
    sessionId: stats.session_id ?? '',
    userScores: Array.isArray(stats.user_scores) ? stats.user_scores.map(mapUserTPAScore) : [],
    teamScore: mapTeamTPAScore(stats.team_score),
    roleScores: Array.isArray(stats.role_scores) ? stats.role_scores.map(mapRoleTPAScore) : [],
    dla: toNumber(stats.dla),
    tla: toNumber(stats.tla),
    rtpi: toNumber(stats.rtpi),
  }
}

const mapSessionAdvice = (advice: any): TpaSessionAdvice | null => {
  if (!advice) return null
  return {
    id: advice.id ?? '',
    session_id: advice.session_id ?? '',
    tpi: advice.tpi ?? 0,
    name: advice.name ?? '',
    description: advice.description ?? '',
    key_question: advice.key_question ?? '',
    recommended_actions: Array.isArray(advice.recommended_actions) ? advice.recommended_actions : [],
    recommended_dynamics: Array.isArray(advice.recommended_dynamics) ? advice.recommended_dynamics : [],
  }
}

const mapSession = (session: any): TpaSession => ({
  id: session.id,
  responses: session.responses,
  phase: session.phase,
  endedAt: session.ended_at,
  createdAt: session.created_at,
  updatedAt: session.updated_at,
  questionnaireStartedAt: session.questionnaire_started_at,
  questionnaireEndedAt: session.questionnaire_ended_at,
  usersReminded: session.users_reminded ? JSON.parse(session.users_reminded) : [],
  resultsChecked: session.results_checked,
  feedbackRequested: session.feedback_requested,
  stats: mapSessionStats(session.stats),
  advice: mapSessionAdvice(session.advice),
})

// ── Service ────────────────────────────────────────────────────────────────────

class SessionService extends ApiClient {
  async fetchByTeam(teamId: string): Promise<TpaSession[]> {
    const response = await this.get<any[]>(`/teams/${teamId}/sessions`)
    return response.data.map(mapSession)
  }

  async fetchById(sessionId: string): Promise<TpaSession> {
    const response = await this.get<any>(`/sessions/${sessionId}`)
    return mapSession(response.data)
  }

  async fetchResults(sessionId: string): Promise<TpaSessionResults> {
    const response = await this.get<TpaSessionResults>(`/sessions/${sessionId}/results`)
    return response.data
  }

  async fetchAdvice(sessionId: string): Promise<TeamPowerAdvice> {
    const response = await this.get<any>(`/sessions/${sessionId}/advice`)
    return response.data as TeamPowerAdvice
  }

  // The endpoint returns plain users now (sourced from user_entities); the
  // TeamUser rows are synthesized - consumers match on `userId` and read
  // `user` for display.
  async fetchMissingQuestionnaireUsers(sessionId: string): Promise<TeamUser[]> {
    if (!sessionId || sessionId.trim() === '') {
      throw new Error('Session ID is required to fetch missing questionnaire users.')
    }
    const response = await this.get<any[]>(
      `/sessions/${sessionId}/missing_questionnaire_users`,
    )
    return (response.data ?? []).map((u: any): TeamUser => ({
      id: u.id,
      teamId: '',
      userId: u.id,
      role: 'team_member',
      user: {
        id: u.id,
        email: u.email,
        firstName: u.first_name ?? u.firstName ?? null,
        lastName: u.last_name ?? u.lastName ?? null,
        language: u.language,
      },
    }))
  }

  async endQuestionnaire(sessionId: string): Promise<void> {
    await this.patch(`/sessions/${sessionId}/questionnaire/end`, undefined, { version: 'v2' })
  }

  async markResultsChecked(sessionId: string): Promise<void> {
    await this.put(`/sessions/${sessionId}/results_checked`)
  }

  async fetchStatus(sessionId: string): Promise<TpaSessionFetchStatusResponse> {
    const response = await this.get<TpaSessionFetchStatusResponse>(`/sessions/${sessionId}/status`)
    return response.data
  }

  async fetchHistorical(teamId: string): Promise<HistoricalTeamResponse> {
    const response = await this.get<any>(`/teams/${teamId}/historical-sessions`, { version: 'v2' })
    return mapHistoricalResponse(response.data)
  }

  async addMember(sessionId: string, teamUser: TeamUserAddPayload): Promise<void> {
    await this.post(
      `/sessions/${sessionId}/user/add`,
      teamUser,
      { version: 'v2' },
    )
  }

  // Session-scoped roster. GET /v2/sessions/:id/members returns plain users
  // (from user_entities); each member's team role is resolved with one
  // batched RBAC lookup (roles are team-scoped, so teamId is required). The
  // result keeps the TeamUser shape every roster consumer renders. Pass
  // includeRemoved=true for the point-in-time historical roster (members
  // whose session link was soft-deleted).
  async fetchMembers(
    sessionId: string,
    teamId: string,
    includeRemoved = false,
  ): Promise<TeamUser[]> {
    const response = await this.get<any[]>(`/sessions/${sessionId}/members`, {
      version: 'v2',
      params: includeRemoved ? { include_removed: true } : undefined,
    })
    const users = (response.data ?? []).map((u: any) => ({
      id: u.id,
      email: u.email,
      firstName: u.first_name ?? u.firstName ?? null,
      lastName: u.last_name ?? u.lastName ?? null,
      language: u.language,
      createdAt: u.created_at ?? u.createdAt,
      updatedAt: u.updated_at ?? u.updatedAt,
    }))
    // Whether each member has been sent the test e-mail this round - a fact
    // about the (member, session) pair, so it lives on the TeamUser, not the user.
    const testEmailSentByUser = new Map<string, boolean>(
      (response.data ?? []).map((u: any): [string, boolean] => [u.id, u.test_email_sent === true]),
    )

    let rolesByUser: Record<string, string[]> = {}
    try {
      rolesByUser = await authService.getRBACRolesBatch(
        users.map((u) => u.id),
        { params: { entity: 'teams', entityId: teamId } },
      )
    } catch (err) {
      console.error('Failed to load session RBAC roles for team', teamId, err)
    }

    const TEAM_ROLE_PRIORITY: TeamRole[] = ['team_leader', 'scrum_master', 'team_member']
    const primaryTeamRole = (roles: string[] | undefined): TeamRole =>
      TEAM_ROLE_PRIORITY.find((r) => roles?.includes(r)) ?? 'team_member'

    return users.map(
      (user): TeamUser => ({
        id: `${sessionId}:${user.id}`,
        teamId,
        userId: user.id,
        role: primaryTeamRole(rolesByUser[user.id]),
        user,
        testEmailSent: testEmailSentByUser.get(user.id) ?? false,
      }),
    )
  }

  // Session-scoped removal (soft-delete of the session link). Only removes
  // the user from THIS session; they remain in the team's other sessions.
  async removeMember(sessionId: string, userId: string): Promise<void> {
    await this.delete(`/sessions/${sessionId}/users/${userId}`, { version: 'v2' })
  }

  // Sends the test e-mail to the given members in one request. The backend
  // sends the first test e-mail to whoever has not been sent it this round and
  // a reminder to whoever has, and answers one outcome per member so one failed
  // recipient never hides the rest.
  async sendTestEmails(sessionId: string, userIds: string[]): Promise<TestEmailResult[]> {
    const response = await this.post<{ results?: any[] }>(
      `/sessions/${sessionId}/test_emails`,
      { user_ids: userIds },
      { version: 'v2' },
    )
    return (response.data?.results ?? []).map(
      (r: any): TestEmailResult => ({
        userId: r.user_id,
        outcome: r.outcome,
        reason: r.reason,
      }),
    )
  }
}

const mapDimensionMap = (raw: any): HPTDimensionMap => {
  if (!raw || typeof raw !== 'object') return {}
  const out: HPTDimensionMap = {}
  for (const key of Object.keys(raw)) {
    out[key as keyof HPTDimensionMap] = toNumber(raw[key])
  }
  return out
}

const mapHistoricalEntry = (raw: any): HistoricalSessionEntry => ({
  sessionId: raw?.session_id ?? '',
  phase: toNumber(raw?.phase),
  startedAt: raw?.started_at ?? '',
  endedAt: raw?.ended_at ?? '',
  questionnaireStartedAt: raw?.questionnaire_started_at ?? '',
  questionnaireEndedAt: raw?.questionnaire_ended_at ?? '',
  tpi: toNumber(raw?.tpi),
  tuckmanStage: raw?.tuckman_stage ?? '',
  tuckmanAdjustedState: raw?.tuckman_adjusted_state ?? '',
  hptTheoretical: mapDimensionMap(raw?.hpt_theoretical),
  hptLeader: mapDimensionMap(raw?.hpt_leader_empirical),
  hptMember: mapDimensionMap(raw?.hpt_member_empirical),
  dla: toNumber(raw?.dla),
  tla: toNumber(raw?.tla),
})

const mapHistoricalResponse = (raw: any): HistoricalTeamResponse => ({
  teamId: raw?.team_id ?? '',
  teamName: raw?.team_name ?? '',
  sessions: Array.isArray(raw?.sessions) ? raw.sessions.map(mapHistoricalEntry) : [],
})

export const sessionService = new SessionService()
